import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Paper } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { isEligibleToJoinProjectAsSubscriberAdmin } from '../project-users/JoinProjectAsSubscriberAdminButton';
import { getUserState } from '../../features/user/selectors';
import { useHistory, useParams } from 'react-router';
import DashboardProjects from './DashboardProjects';
import DashboardCountdown from './DashboardCountdown';
import DashboardMyTasksCard from './DashboardMyTasksCard';
import DashboardMessageBoards from './DashboardMessageBoards';
import DashboardMessageBoard from './DashboardMessageBoard';
import DashboardChanges from './DashboardChanges';
import DashboardFieldTools from './DashboardFieldTools';
import NavAppbar from '../nav-top/NavAppbar';
import DashboardDoughnut from './DashboardDoughnut';
import { getProjectState } from '../../features/project/selectors';
import { fetchProject, setProject } from '../../features/project/actions';
import { canViewConstructionAndCloseout, ProjectMatchParams } from '../../scripts/utils';
import { getProjectLoadingState } from '../../features/loading/selectors';
import FullscreenLoader from '../loader/FullscreenLoader';
import { isMobile } from 'react-device-detect';
import { setDocumentsType } from '../../features/documents/actions';
import { getProductPackageState } from '../../features/product-package/selectors';
import { DocumentTemplateType } from '../../api-client/autogenerated';
import DashboardBidding from './DashboardBidding';
import DashboardEmails from './DashboardEmails';
import { hasCompletedConstructionSetup, hasSearchPermissions } from '../../scripts/store-utils';
import DashboardSearch from './DashboardSearch';
import getProjectsState from '../../features/projects/selectors';
import SubscriberAdminJoinProjectCard from './SubscriberAdminJoinProjectCard';

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    minHeight: '100vh',
    overflowX: 'hidden',
  },
  paper: {
    color: theme.palette.text.secondary,
    height: '100%',
    boxShadow: '5px 5px 15px rgb(0, 0, 0, .15)',
  },
  paperNoFill: {
    color: theme.palette.text.secondary,
    boxShadow: '5px 5px 15px rgb(0, 0, 0, .15)',
  },
  paperDefaultHeight: {
    color: theme.palette.text.secondary,
    height: '100%',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

export default function Dashboard() {
  const classes = useStyles();

  const params = useParams<ProjectMatchParams>();
  const dispatch = useDispatch();
  const history = useHistory();
  const productPackage = useSelector(getProductPackageState);
  const projects = useSelector(getProjectsState);
  const project = useSelector(getProjectState);
  const projectLoading = useSelector(getProjectLoadingState);
  const user = useSelector(getUserState);
  const showSubscriberAdminJoinCard = isEligibleToJoinProjectAsSubscriberAdmin(user, project);

  useEffect(() => {
    if (params.projectId !== project?.id && !projectLoading) {
      dispatch(fetchProject(params.projectId)).then((action: any) => {
        if (!action?.payload) history.push('/main');
      });
    } else if (!params.projectId) dispatch(setProject());

    dispatch(setDocumentsType(null));
  }, [params, projectLoading]);

  const hasSearchPermission = hasSearchPermissions(projects, project?.id);

  if (projectLoading) return <FullscreenLoader />;

  return (
    <main className={classes.content}>
      <NavAppbar />
      <div>
        {project ? (
          <Grid container spacing={2} alignItems="stretch" direction="row">
            <Grid item xs={12} lg={hasSearchPermission ? 6 : 12}>
              <div className={classes.column}>
                <Paper className={classes.paperNoFill}>
                  <DashboardCountdown />
                </Paper>
              </div>
            </Grid>
            {showSubscriberAdminJoinCard ? (
              <Grid item xs={12} lg={3}>
                <Paper className={classes.paperNoFill} style={{ height: '100%' }}>
                  <SubscriberAdminJoinProjectCard />
                </Paper>
              </Grid>
            ) : null}
            <Grid item xs={12} lg={showSubscriberAdminJoinCard ? 3 : 6}>
              <div className={classes.column}>
                {!isMobile && hasSearchPermission ? (
                  <Paper className={classes.paperNoFill}>
                    <DashboardSearch />
                  </Paper>
                ) : null}
                {isMobile &&
                canViewConstructionAndCloseout(productPackage) &&
                hasCompletedConstructionSetup() ? (
                  <Paper className={classes.paper}>
                    <DashboardFieldTools />
                  </Paper>
                ) : null}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <DashboardMyTasksCard />
              </Paper>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Paper className={classes.paper}>
                <DashboardBidding />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper
                className={classes.paperDefaultHeight}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <DashboardMessageBoard />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper
                className={classes.paperDefaultHeight}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <DashboardEmails />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                {/* Submittal */}
                <DashboardDoughnut type={DocumentTemplateType.Submittals} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                {/* RFI */}
                <DashboardDoughnut type={DocumentTemplateType.RequestsForInformation} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper style={{ height: 'auto' }} className={classes.paper}>
                <DashboardChanges />
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2} alignItems="stretch" direction="row">
            <Grid item xs={12} style={{ display: 'inline-flex' }}>
              <Paper className={classes.paper} style={{ width: '100%' }}>
                <DashboardProjects />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <DashboardMyTasksCard />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              {!isMobile && hasSearchPermission ? (
                <Paper className={classes.paperNoFill} style={{ marginBottom: 16 }}>
                  <DashboardSearch />
                </Paper>
              ) : null}

              <Paper className={classes.paper} style={{ height: 560 }}>
                <DashboardEmails />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Paper className={classes.paper} style={{ height: 700 }}>
                <DashboardMessageBoards />
              </Paper>
            </Grid>
          </Grid>
        )}
      </div>
    </main>
  );
}
